import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CallMetricsApiService } from '../services/call-metrics-api.service';
import {
  AgentCallSummaryResponse,
  CallMetricsResponse,
  MetricsPeriodFilter
} from '../models/call-metrics.model';
import { ToastService } from '../../../shared/services/toast.service';
import { AGENT_PRESENCE_LABELS, labelFrom } from '../call-labels';

@Component({
  selector: 'app-call-metrics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './call-metrics.component.html'
})
export class CallMetricsComponent implements OnInit {
  loading = false;
  loadingAgents = false;
  exporting = false;
  errorMsg = '';

  dashboard: CallMetricsResponse | null = null;
  agents: AgentCallSummaryResponse[] = [];

  // Período por defecto: últimos 30 días.
  fromDate = '';
  toDate = '';

  presenceLabel(status: string | null | undefined): string {
    return labelFrom(AGENT_PRESENCE_LABELS, status, 'Desconectado');
  }

  constructor(
    private readonly api: CallMetricsApiService,
    private readonly toast: ToastService
  ) {
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - 30);
    this.fromDate = from.toISOString().slice(0, 10);
    this.toDate = now.toISOString().slice(0, 10);
  }

  ngOnInit(): void {
    this.refrescar();
    this.cargarAgentes();
  }

  refrescar(): void {
    this.loading = true;
    this.errorMsg = '';
    this.api.getDashboard(this.periodFilter()).subscribe({
      next: (res) => {
        this.dashboard = res;
        this.loading = false;
      },
      error: (err) => {
        this.errorMsg = err.error?.message || 'Error al cargar las métricas';
        this.loading = false;
      }
    });
  }

  cargarAgentes(): void {
    this.loadingAgents = true;
    this.api.listAgentMetrics().subscribe({
      next: (res) => {
        this.agents = Array.isArray(res) ? res : [];
        this.loadingAgents = false;
      },
      error: () => {
        this.agents = [];
        this.loadingAgents = false;
      }
    });
  }

  exportarCsv(): void {
    this.exporting = true;
    this.api.exportMetrics(this.periodFilter(), 'csv').subscribe({
      next: (blob) => {
        this.descargarBlob(blob, `call-metrics-${this.fromDate}_${this.toDate}.csv`);
        this.exporting = false;
        this.toast.success({ title: 'Exportación lista', message: 'El CSV se descargó.' });
      },
      error: (err) => {
        this.exporting = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo exportar las métricas' });
      }
    });
  }

  private descargarBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private periodFilter(): MetricsPeriodFilter {
    const out: MetricsPeriodFilter = {};
    if (this.fromDate) out.periodStart = `${this.fromDate}T00:00:00Z`;
    if (this.toDate) out.periodEnd = `${this.toDate}T23:59:59Z`;
    return out;
  }

  formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || isNaN(seconds)) return '—';
    const s = Math.floor(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
    return `${m}m ${ss.toString().padStart(2, '0')}s`;
  }

  formatDate(iso?: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-PE', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  }

  answerRate(): number {
    if (!this.dashboard || this.dashboard.totalCalls === 0) return 0;
    return Math.round((this.dashboard.answeredCalls / this.dashboard.totalCalls) * 100);
  }

  agentAnswerRate(a: AgentCallSummaryResponse): number {
    if (a.totalCalls === 0) return 0;
    return Math.round((a.answeredCalls / a.totalCalls) * 100);
  }

  trackByAgentId = (_: number, a: AgentCallSummaryResponse) => a.agentId;
}
